import { useState, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { UserProfile } from '../types/user';
import { toCamelCase, toSnakeCase } from '../utils/caseConversion';

export const useUserProfile = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    // Pattern recommandé Supabase : SEULEMENT onAuthStateChange
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted.current) return;

        console.log('🔐 Auth event:', event, session?.user?.email || 'guest');

        setUser(session?.user ?? null);

        if (session?.user) {
          // Check if this is an OAuth redirect with language parameter
          // Update user metadata with language for future emails
          if (event === 'SIGNED_IN') {
            const urlParams = new URLSearchParams(window.location.search);
            const langFromUrl = urlParams.get('lang');
            const currentLang = session.user.user_metadata?.lang;

            // If we have lang in URL and user doesn't have it in metadata, update
            if (langFromUrl && !currentLang) {
              console.log('🌐 Setting user language from OAuth redirect:', langFromUrl);
              supabase.auth.updateUser({
                data: { lang: langFromUrl }
              }).then(({ error }) => {
                if (error) {
                  console.warn('⚠️ Could not update user language:', error.message);
                } else {
                  console.log('✅ User language updated to:', langFromUrl);
                }
              });
              // Clean up URL parameter
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete('lang');
              window.history.replaceState({}, '', newUrl.toString());
            }
          }

          // User connecté : charger le profil avec timeout de sécurité
          loadProfileWithTimeout(session.user.id, session.user.email);
        } else {
          // Pas de user : mode guest
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadProfileWithTimeout = async (userId: string, userEmail?: string) => {
    // Timeout de sécurité : 5 secondes max
    const timeoutId = setTimeout(() => {
      if (!isMounted.current) return;
      console.warn('⚠️ Timeout 5s : profil non chargé, continuation en mode dégradé');
      setProfile(null);
      setLoading(false);
    }, 5000);

    try {
      // Délai pour laisser le SDK charger le token
      await new Promise(resolve => setTimeout(resolve, 100));

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      clearTimeout(timeoutId);

      if (!isMounted.current) return;

      if (error) {
        console.error('❌ Erreur profil:', error);

        // Si profil n'existe pas, le créer
        if ('code' in error && error.code === 'PGRST116') {
          await createProfile(userId, userEmail);
        } else {
          setProfile(null);
          setLoading(false);
        }
      } else if (data) {
        console.log('✅ Profil chargé depuis Supabase:', {
          subscriptionStatus: data.subscription_status,
          subscriptionTier: data.subscription_tier,
          trialEndsAt: data.trial_ends_at
        });
        setProfile(toCamelCase(data));
        setLoading(false);
      } else {
        // Pas de data : créer le profil
        await createProfile(userId, userEmail);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('❌ Exception profil:', err);
      if (isMounted.current) {
        setProfile(null);
        setLoading(false);
      }
    }
  };

  const createProfile = async (userId: string, userEmail?: string) => {
    try {
      const defaultUsername = userEmail?.split('@')[0] || 'User';
      const { data: newProfile, error } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          username: defaultUsername,
        })
        .select()
        .single();

      if (!isMounted.current) return;

      if (error) {
        console.error('❌ Erreur création profil:', error);
        setProfile(null);
      } else {
        console.log('✅ Profil créé');
        setProfile(toCamelCase(newProfile));
      }
    } catch (err) {
      console.error('❌ Exception création profil:', err);
      if (isMounted.current) setProfile(null);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    try {
      const updatesSnakeCase = toSnakeCase(updates);

      const { error } = await supabase
        .from('user_profiles')
        .update(updatesSnakeCase)
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }

      setProfile(prev => (prev ? { ...prev, ...updates } : null));
    } catch (err) {
      console.error('Failed to update profile:', err);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      // Utiliser scope: 'local' pour éviter l'appel serveur qui peut échouer (403)
      const { error } = await supabase.auth.signOut({ scope: 'local' });

      if (error) {
        console.warn('⚠️ SignOut warning:', error.message);
      }
    } catch (err) {
      console.warn('⚠️ SignOut exception (ignorée):', err);
    }

    // Forcer le nettoyage du localStorage (Supabase + LilHero)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase') || key.startsWith('lilhero_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log('🗑️ Removing localStorage key:', key);
      localStorage.removeItem(key);
    });

    // Nettoyer l'état local
    setUser(null);
    setProfile(null);
    setLoading(false);
  };

  // Get auth provider from user identities
  const getAuthProvider = (): string | undefined => {
    if (!user) return undefined;
    // Check identities array (most reliable)
    if (user.identities && user.identities.length > 0) {
      return user.identities[0].provider;
    }
    // Fallback to app_metadata
    return user.app_metadata?.provider;
  };

  return {
    user,
    profile,
    loading,
    updateProfile,
    signOut,
    isAuthenticated: !!user,
    isPremium: profile?.subscriptionTier === 'premium' || profile?.subscriptionTier === 'family',
    emailConfirmed: !!user?.email_confirmed_at,
    authProvider: getAuthProvider(),
  };
};

export default useUserProfile;
